import React from 'react';
import LegalLayout from '../../components/LegalLayout';
import { COMPANY, LAST_UPDATED } from './companyConfig';

const RefundPolicy = () => (
  <LegalLayout
    title="Refund Policy"
    subtitle="How refunds work at ACF Tourism — the amounts, timelines, and what to expect."
    lastUpdated={LAST_UPDATED}
    canonicalPath="/refund-policy"
    description="ACF Tourism refund policy — timelines, airline vs platform charges, and how refunds are returned to your original payment method."
  >
    <h2>1. Principle</h2>
    <p>
      Refunds are processed back to the <strong>original payment source</strong> used at the
      time of booking. {COMPANY.brand} does not issue refunds to a different card, bank
      account, or wallet except where mandated by law or where the original source is no
      longer active.
    </p>

    <h2>2. What Is Refundable</h2>
    <p>
      A refund consists of three potentially separate components:
    </p>
    <table>
      <thead>
        <tr><th>Component</th><th>Refundable?</th><th>Notes</th></tr>
      </thead>
      <tbody>
        <tr>
          <td>Airline base fare + taxes (YQ / YR / K3 / OC)</td>
          <td>As per airline fare rules</td>
          <td>Pass-through. Airline cancellation charges apply.</td>
        </tr>
        <tr>
          <td>{COMPANY.brand} service fee</td>
          <td>Conditional</td>
          <td>
            Refunded in full for full cancellations; reduced by our cancellation fee for
            partial cancellations (see <a href={COMPANY.urls.cancellation}>Cancellation Policy</a>).
          </td>
        </tr>
        <tr>
          <td>Payment gateway charges</td>
          <td>Non-refundable</td>
          <td>Gateway bank charges (typically 1–3%) are retained by the acquiring bank.</td>
        </tr>
      </tbody>
    </table>

    <h2>3. Processing Timeline</h2>
    <p>
      Once a refund is approved, it is <strong>initiated by us within 5 business days</strong>.
      The amount will reflect in your account based on your payment method:
    </p>
    <ul>
      <li><strong>Credit / debit cards:</strong> 7 – 10 business days</li>
      <li><strong>Net-banking:</strong> 5 – 7 business days</li>
      <li><strong>UPI / wallets:</strong> 3 – 5 business days</li>
      <li><strong>Agent wallet:</strong> Instant credit, subject to admin approval</li>
    </ul>
    <p>
      These timelines are bank-dependent. If you do not see the refund after the stated
      period, please contact your bank first with the ARN (reference number) we provide,
      then escalate to us if the bank confirms non-receipt.
    </p>

    <h2>4. Credit Notes & GST Reversal</h2>
    <p>
      For B2B agents with valid GSTIN, every refund is accompanied by a
      <strong> GST Credit Note</strong> issued under Section 34 of the CGST Act. The Credit
      Note reverses GST only on the refunded portion of our service fee — retained
      cancellation fee remains taxed under the original tax invoice.
    </p>

    <h2>5. Non-Refundable Situations</h2>
    <ul>
      <li>Fares marked <strong>non-refundable</strong> at time of booking (typically promotional / deep-discount fares).</li>
      <li>No-shows at the airport without prior cancellation.</li>
      <li>Denial of boarding due to invalid / expired travel documents.</li>
      <li>Cancellations post-departure of the first flight segment.</li>
      <li>Ancillary services (seat selection, meal, baggage) once availed or after check-in.</li>
    </ul>

    <h2>6. Partial Refunds</h2>
    <p>
      When only part of a multi-segment journey is cancelled, the refundable amount is
      calculated on the unused portion as per the airline's rebooking / prorated-fare
      rules. Our service fee for the cancelled segments is refundable net of the applicable
      cancellation fee.
    </p>

    <h2>7. How to Request a Refund</h2>
    <ol>
      <li>Log in and open <strong>My Bookings</strong>.</li>
      <li>Select the booking and click <em>“Need Help”</em> → <em>Cancellation / Refund</em>.</li>
      <li>Our support team will confirm the refundable amount within 24 hours and initiate the refund once approved.</li>
    </ol>
    <p>
      If you cannot access your account, email{' '}
      <a href={`mailto:${COMPANY.supportEmail}`}>{COMPANY.supportEmail}</a> from the email
      address used at the time of booking with your booking reference and a government ID.
    </p>

    <h2>8. Disputes / Chargebacks</h2>
    <p>
      Please raise a refund request with us before initiating a chargeback with your bank.
      We take chargebacks seriously and will share the full audit trail (search logs,
      authorisation, confirmation, e-ticket) with the acquiring bank. Chargebacks found to
      be without merit may result in account suspension.
    </p>

    <h2>9. Contact</h2>
    <p>
      Refund-related queries:{' '}
      <a href={`mailto:${COMPANY.supportEmail}`}>{COMPANY.supportEmail}</a> ·{' '}
      {COMPANY.phone} ({COMPANY.phoneHours}).
    </p>
  </LegalLayout>
);

export default RefundPolicy;
